import { getAccountName } from '@interfaces/account';
import { AffiliationTypes } from '@interfaces/affiliationTypes';
import type { FinancialProduct, Theme } from '@interfaces/financialProduct';
import { getGoalName } from '@interfaces/goal';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Form } from '@ovComponents/2-component/form/form';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { legacyColors } from '@ovComponents/legacyColor';
import { generateClientNameString } from '@utils/generateClientNameString';
import { Box, Button, Grid, Typography } from 'hephaestus/web';
import type { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DraftFinancialProduct } from './interfaces';

export const FinancialProductSelectionForm = ({
  draftFinancialProduct,
  portfolioThemes,
  modelPortfolios,
  updateDraft,
  onPortfolioThemeChange,
  onClose,
  onSubmit,
}: {
  draftFinancialProduct?: DraftFinancialProduct;
  portfolioThemes: Theme[];
  modelPortfolios: FinancialProduct[];
  updateDraft: (subAccountId: string, draft: DraftFinancialProduct) => void;
  onPortfolioThemeChange: (theme: Theme) => void;
  onClose: () => void;
  onSubmit: () => void;
}) => {
  const { t } = useTranslation([
    'accountTypes',
    'client',
    'workflowCompletions',
  ]);
  if (!draftFinancialProduct)
    return (
      <Typography variant='bodyLarge' sx={{ textAlign: 'center' }}>
        No content
      </Typography>
    );
  const { subAccount } = draftFinancialProduct;
  const { goal, account } = subAccount;
  const title = t('workflowCompletions:subAccounts.formTitle', {
    goalName: getGoalName(goal),
    accountName: getAccountName(account, false),
  });
  const isSubmissionDisabled = !(
    draftFinancialProduct.subAccount && draftFinancialProduct.financialProduct
  );

  const handlePortfolioThemeChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const newPortfolioTheme = portfolioThemes.find(
      (item) => item.id === e.target.value,
    );
    updateDraft(subAccount.id, {
      ...draftFinancialProduct,
      theme: newPortfolioTheme,
    });
    if (newPortfolioTheme) onPortfolioThemeChange(newPortfolioTheme);
  };

  const handleModelPortfolioChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const newModelPortfolio = modelPortfolios.find(
      (item) => item.id === e.target.value,
    );
    updateDraft(subAccount.id, {
      ...draftFinancialProduct,
      financialProduct: newModelPortfolio,
    });
  };

  const handleSubmit = () => {
    onSubmit();
    onClose();
  };

  const getSuitabilityScoreElement = (
    text: string,
    score: number,
  ): ReactNode => {
    if (text && score !== undefined) {
      return (
        <Grid
          size={12}
          sx={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Typography
            variant='labelLarge'
            component='div'
            sx={{ color: legacyColors.neutral800 }}
          >
            {text}:&nbsp;
          </Typography>
          <Typography
            variant='labelLarge'
            component='div'
            sx={{ fontWeight: 600, color: legacyColors.neutral800 }}
          >
            {score}
          </Typography>
        </Grid>
      );
    }
    return <></>;
  };

  const suitabilityScoresElement = (
    <>
      {getSuitabilityScoreElement(
        t('workflowCompletions:goals.suitability.individualSuitabilityScore', {
          name: generateClientNameString(account.user, true),
        }),
        account.user.suitabilityScore ?? 0,
      )}
      {(account?.affiliations ?? [])
        .filter((affiliate) => affiliate.type === AffiliationTypes.Joint)
        .map((affiliate) =>
          getSuitabilityScoreElement(
            t(
              'workflowCompletions:goals.suitability.individualSuitabilityScore',
              { name: generateClientNameString(affiliate.user, true) },
            ),
            affiliate.user.suitabilityScore ?? 0,
          ),
        )}
      {getSuitabilityScoreElement(
        t('workflowCompletions:goals.suitability.goalSuitabilityScore'),
        goal.suitabilityScore ?? 0,
      )}
    </>
  );

  return (
    <Form onSubmit={handleSubmit}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
        }}
      >
        <Box
          onClick={onClose}
          sx={{
            display: 'flex',
            cursor: 'pointer',
          }}
        >
          <Icon icon={CloseRoundedIcon} size='medium' />
        </Box>
      </Box>
      <Typography variant='headingMedium' sx={{ mt: 1 }}>
        {title}
      </Typography>
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid container spacing={1} size={12}>
          <Grid>
            <Typography
              variant='headingXSmall'
              sx={{ color: legacyColors.neutral800 }}
            >
              {t('workflowCompletions:subAccounts.suitability.title')}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              variant='bodyMedium'
              sx={{ color: legacyColors.neutral600, fontWeight: 500 }}
            >
              {t('workflowCompletions:subAccounts.suitability.info')}
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={1} size={12}>
          {suitabilityScoresElement}
        </Grid>
        <Grid size={12}>
          <SelectField
            label={t('workflowCompletions:goals.portfolioTheme')}
            value={draftFinancialProduct.theme?.id}
            onChange={handlePortfolioThemeChange}
            fullWidth
          >
            {portfolioThemes.map((item, index) => (
              <MenuItem key={index} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </SelectField>
        </Grid>
        {draftFinancialProduct.theme && (
          <Grid size={12}>
            <SelectField
              label={t('workflowCompletions:goals.portfolioModel')}
              value={draftFinancialProduct.financialProduct?.id}
              onChange={handleModelPortfolioChange}
              fullWidth
            >
              {modelPortfolios.map((item, index) => (
                <MenuItem key={index} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
        )}
        <Grid size={12}>
          <SwitchField
            label={t('workflowCompletions:subAccounts.requireIPS')}
            checked={!draftFinancialProduct.skipIPS}
            onChange={(onoff: boolean) => {
              updateDraft(subAccount.id, {
                ...draftFinancialProduct,
                skipIPS: onoff,
              });
            }}
            labelPosition='left'
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <SwitchField
            label={t('workflowCompletions:subAccounts.allowFractionalShares')}
            checked={draftFinancialProduct.isPartial}
            onChange={(onoff: boolean) => {
              updateDraft(subAccount.id, {
                ...draftFinancialProduct,
                isPartial: onoff,
              });
            }}
            labelPosition='left'
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <SwitchField
            label={t('workflowCompletions:subAccounts.allowClientDeposits')}
            checked={draftFinancialProduct.allowClientDeposits}
            onChange={(onoff: boolean) => {
              updateDraft(subAccount.id, {
                ...draftFinancialProduct,
                allowClientDeposits: onoff,
              });
            }}
            labelPosition='left'
            fullWidth
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            justifyContent: 'end',
            display: 'flex',
          }}
        >
          <Button type='submit' disabled={isSubmissionDisabled} color='primary'>
            {t('workflowCompletions:subAccounts.linkSubAccount')}
          </Button>
        </Grid>
      </Grid>
    </Form>
  );
};
